import { useCallback, useEffect, useState } from "react"
import { toaster } from "../../chakra/Toaster"
import { formatChipLabel, getTimeWindowDisplayValue, parseTimeWindowInput } from "./alertRuleFormat"
import type { AlertRuleState } from "./AlertRuleCard"

/** The API's reason for refusing a change, when it gave one: not a network failure. */
function refusal(error: unknown): string | undefined {
  return (error as { graphQLErrors?: Array<{ message?: string }> })?.graphQLErrors?.[0]?.message
}

export interface AlertRuleHandlers {
  onToggle: (enabled: boolean) => void | Promise<void>
  onUpdateThreshold: (value: number) => void | Promise<void>
  onUpdateTimeWindow: (value: number) => void | Promise<void>
  onReset: () => void | Promise<void>
}

/**
 * State and save handlers for one alert rule, with their toasts. Shared by every layout
 * that renders a rule (the Systems card, the Assets alert card), so they behave the same.
 * The threshold, time-window and reset handlers resolve to whether the change saved.
 */
export function useAlertRuleControls(
  rule: AlertRuleState,
  { onToggle, onUpdateThreshold, onUpdateTimeWindow, onReset }: AlertRuleHandlers,
) {
  const { template, assignment, isEnabled, isOverridden, effectiveThreshold } = rule
  const [resetting, setResetting] = useState(false)
  const [isToggling, setIsToggling] = useState(false)
  const [localThreshold, setLocalThreshold] = useState<string | null>(null)
  const [localTimeWindow, setLocalTimeWindow] = useState<string | null>(null)

  const effectiveTimeWindow = assignment?.timeWindowOverrideSeconds ?? template.timeWindowSeconds

  useEffect(() => {
    setResetting(false)
    setLocalThreshold(null)
    setLocalTimeWindow(null)
  }, [effectiveThreshold, assignment?.timeWindowOverrideSeconds])

  const showAsOverridden = isOverridden && !resetting

  const displayThreshold = localThreshold ?? effectiveThreshold?.toString() ?? ""
  const displayTimeWindow = localTimeWindow != null
    ? localTimeWindow
    : effectiveTimeWindow != null
      ? getTimeWindowDisplayValue(effectiveTimeWindow).value.toString()
      : null
  const timeWindowUnit = effectiveTimeWindow != null ? getTimeWindowDisplayValue(effectiveTimeWindow).unit : "h"

  const handleToggle = useCallback(
    async (checked: boolean) => {
      if (isToggling) return
      setIsToggling(true)
      try {
        await onToggle(checked)
        if (checked) {
          toaster.success({
            title: "Alert activated",
            description: `'${template.name}' is now active.`,
          })
        } else {
          toaster.success({
            title: "Alert deactivated",
            description: `'${template.name}' has been deactivated.`,
          })
        }
      } catch (error) {
        toaster.error({
          title: checked ? "Failed to activate alert" : "Failed to deactivate alert",
          description:
            refusal(error) ??
            "We couldn't update this alert setting. Please check your network connection and try again.",
        })
      } finally {
        setIsToggling(false)
      }
    },
    [isToggling, onToggle, template.name],
  )

  const handleThresholdCommit = useCallback(
    async (newValue: string) => {
      const num = parseFloat(newValue)
      if (isNaN(num)) return false
      if (num === effectiveThreshold) return true

      try {
        await onUpdateThreshold(num)
        const unit = template.thresholdUnit ?? ""
        const direction = template.thresholdDirection ? `${formatChipLabel(template.thresholdDirection)} ` : ""
        toaster.success({
          title: "Threshold updated",
          description: `'${template.name}' threshold set to ${direction}${num}${unit}.`,
        })
        return true
      } catch {
        setLocalThreshold(effectiveThreshold?.toString() ?? null)
        toaster.error({
          title: "Failed to update threshold",
          description: "We couldn't save your threshold change. Please check your network connection and try again.",
        })
        return false
      }
    },
    [effectiveThreshold, onUpdateThreshold, template.name, template.thresholdUnit, template.thresholdDirection],
  )

  const handleTimeWindowCommit = useCallback(
    async (newValue: string) => {
      if (!effectiveTimeWindow) return false
      const { unit } = getTimeWindowDisplayValue(effectiveTimeWindow)
      const seconds = parseTimeWindowInput(newValue, unit)
      if (seconds == null) return false
      if (seconds === effectiveTimeWindow) return true

      try {
        await onUpdateTimeWindow(seconds)
        const displayVal = getTimeWindowDisplayValue(seconds)
        toaster.success({
          title: "Time window updated",
          description: `'${template.name}' time window set to ${displayVal.value} ${displayVal.unit}.`,
        })
        return true
      } catch {
        setLocalTimeWindow(getTimeWindowDisplayValue(effectiveTimeWindow).value.toString())
        toaster.error({
          title: "Failed to update time window",
          description: "We couldn't save your time window change. Please check your network connection and try again.",
        })
        return false
      }
    },
    [effectiveTimeWindow, onUpdateTimeWindow, template.name],
  )

  const handleReset = useCallback(async () => {
    setResetting(true)
    try {
      await onReset()
      setLocalThreshold(null)
      setLocalTimeWindow(null)
      toaster.success({
        title: "Reset to defaults",
        description: `'${template.name}' restored to default template values.`,
      })
      return true
    } catch {
      toaster.error({
        title: "Failed to reset alert",
        description: "We couldn't restore default values. Please check your network connection and try again.",
      })
      return false
    } finally {
      setResetting(false)
    }
  }, [onReset, template.name])

  return {
    isToggling,
    resetting,
    showAsOverridden,
    canEdit: !isEnabled && template.overridable,
    displayThreshold,
    displayTimeWindow,
    timeWindowUnit,
    handleToggle,
    handleThresholdCommit,
    handleTimeWindowCommit,
    handleReset,
  }
}
